/**
 * Custom Heading extension with NodeView for TipTap.
 * Adds nodeId and locked attributes.
 */

import { useCallback } from "react";
import Heading from "@tiptap/extension-heading";
import { NodeViewWrapper, NodeViewContent, ReactNodeViewRenderer } from "@tiptap/react";
import type { ReactNodeViewProps } from "@tiptap/react";

function HeadingComponent({ node, editor, getPos }: ReactNodeViewProps) {
  const locked = node.attrs.locked || false;
  const level = node.attrs.level || 2;

  // Handle clicks on empty space - put cursor at end of text
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("[data-node-view-content]")) return;

      const pos = getPos();
      if (pos === undefined) return;

      e.preventDefault();
      editor.view.focus();
      const end = pos + node.nodeSize - 1;
      editor.commands.setTextSelection(end);
    },
    [editor, getPos, node.nodeSize]
  );

  const Tag = `h${level}` as keyof JSX.IntrinsicElements;

  return (
    <NodeViewWrapper
      as={Tag}
      className={locked ? "locked" : ""}
      onMouseDown={handleMouseDown}
    >
      <NodeViewContent />
    </NodeViewWrapper>
  );
}

const CustomHeading = Heading.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      nodeId: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-node-id"),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes.nodeId) return {};
          return { "data-node-id": attributes.nodeId };
        },
      },
      locked: {
        default: false,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-locked") === "true",
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes.locked) return {};
          return { "data-locked": "true" };
        },
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(HeadingComponent);
  },
});

export default CustomHeading;
